import React, { createContext, useContext, useEffect, useState } from 'react';

// Minimal cart store — items live in localStorage so they survive reloads
// and cross-page navigation. No server persistence yet (charms parity gap);
// future iteration syncs to /storefront/<cid>/cart for signed-in shoppers.

const CartContext = createContext(null);
const STORAGE_KEY = 'oh:cart:v1';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) { return []; }
}

function writeStored(items) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) {}
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readStored());
  useEffect(() => { writeStored(items); }, [items]);

  useEffect(() => {
    function onStorage(e) { if (e.key === STORAGE_KEY) setItems(readStored()); }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addItem = (product, quantity = 1) => {
    if (!product) return;
    setItems((cur) => {
      const idx = cur.findIndex((it) => String(it.id) === String(product.id));
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + quantity };
        return next;
      }
      return [...cur, {
        id: product.id,
        name: product.name,
        brand: product.brand || '',
        price: typeof product.price === 'number' ? product.price : 0,
        originalPrice: product.originalPrice ?? null,
        image: product.image || product.image_url || '',
        imageAlt: product.imageAlt || product.name || '',
        quantity,
      }];
    });
  };

  const updateQuantity = (id, quantity) => {
    setItems((cur) => cur.map((it) =>
      String(it.id) === String(id) ? { ...it, quantity: Math.max(1, quantity) } : it
    ));
  };
  const removeItem = (id) => setItems((cur) => cur.filter((it) => String(it.id) !== String(id)));
  const clearCart  = () => setItems([]);

  const totalCount = items.reduce((sum, it) => sum + (it.quantity || 1), 0);
  const totalCents = Math.round(items.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1) * 100, 0));

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, removeItem, clearCart, totalCount, totalCents }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
